import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { FiUpgradeCountdownNotificationService } from '@fi-app/utilities/upgrade-countdown-notification/upgrade-countdown-notification.service';
import { NewsFeedService } from '@fi-app/news-feed/news-feed.service';
import { StatisticService } from '@fi-app/shared/statistic/statistic.service';
import { GlobalSearchService } from '@fi-app/components/global-search/global-search.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { CcmSessionData } from '@fi-app/components/core-base';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiAuthenticateModuleService } from '@fi-app/authenticate/authenticate-module.service';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isIOS } from '@fi-shared/utils/isIOS';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { takeUntil } from 'rxjs/operators';
import { IUpgradeLogEntry } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-app',
    templateUrl: './fi-app.component.html',
    standalone: false,
})
export class FiAppComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isCompactMode: boolean;
    @Output() loadedChange = new EventEmitter<boolean>();
    @Output() loginContentChange = new EventEmitter<boolean>();
    @Output() bodyClassChange = new EventEmitter<string>();
    @Output() loadedStatusTextChange = new EventEmitter<string>();

    private _loaded = true;
    get Loaded(): boolean {
        return this._loaded;
    }
    set Loaded(value: boolean) {
        this._loaded = value;
        this.emitChanges();
    }

    private _loadedStatusText: string;
    get loadedStatusText(): string {
        return this._loadedStatusText;
    }
    set loadedStatusText(value: string) {
        this._loadedStatusText = value;
        this.loadedStatusTextChange.emit(this.loadedStatusText);
    }

    private globalLoading = false; // true
    private ShowSearch = false;
    private userSetIsCompactMode = false;

    constructor(
        private stateService: FiStateService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private contentLanguagesService: ContentLanguagesService,
        private localeService: FiLocaleService,
        private upgradeCountdownService: FiUpgradeCountdownNotificationService,
        private newsFeedService: NewsFeedService,
        private fiSignalRService: FiSignalRService,
        private statisticService: StatisticService,
        private globalSearchService: GlobalSearchService,
        private fiLoadingService: LoadingService,
        private appTextService: AppTextService,
        private renderer: Renderer2,
        private loadingService: LoadingService,
        private authModuleService: FiAuthenticateModuleService,
        private fiStateChangeService: FiStateChangeService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {
        this.bodyClassChange.subscribe((bodyClass) => {
            this.renderer.setAttribute(document.body, 'class', bodyClass);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.isCompactMode;
        if (change && !change.firstChange) {
            this.userSetIsCompactMode = this.isCompactMode;
            this.checkWidth();
        }
    }

    public emitChanges(): void {
        this.loadedChange.emit(this.Loaded);
        this.loginContentChange.emit(this.IsLoginContent());
        this.bodyClassChange.emit(this.getBodyClass());
    }

    ngOnInit(): void {
        this.loadedStatusText = 'Waiting for service...';

        /*
        this.mobileAppHelperService.mobileAppMode = sessionStorage.getItem('fic-app-mobile') === '1'
            || this.stateService.current.name === FiStateNames.Login.Token
            || window.location.pathname === '/LoginToken';

*/
        this.initStateWatchers();

        this.globalSearchService.onShowGlobalSearch((data: boolean) => {
            this.ShowSearch = data;
        });

        this.fiLoadingService.onToggleLoading((loading) => this.ToggleLoading(loading));

        this.loadedStatusText = 'Loading translations...';

        this.localeService.setLanguage(this.userDataService.applicationLanguage);

        this.contentLanguagesService.UpdateContentLanguageNames();

        // start upgrade countdown in case new version is available
        if (
            this.customerDataService.customerData.UpcomingVersion &&
            this.customerDataService.customerData.UpcomingVersion.UpgradeStatusType == 1
        )
            this.upgradeCountdownService.StartUpgradeCountDown(this.customerDataService.customerData.UpcomingVersion);

        if (!this.isFromMobileApp()) this.checkLoading();

        this.fiSignalRService.newVersionUpgradeEvent.subscribe((upgradeLogEntry: IUpgradeLogEntry) => {
            setTimeout(() => {
                upgradeLogEntry.UpgradeStatusType === 1
                    ? this.upgradeCountdownService.StartUpgradeCountDown(upgradeLogEntry)
                    : this.upgradeCountdownService.CancelUpgradeCountDown();
            });
        });

        this.checkWidth();
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.checkWidth());
    }

    private isFromMobileApp(): boolean {
        return this.stateService.current.name === FiStateNames.LoginToken;
    }

    private checkWidth(): void {
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        //Collapse the dashboard if it's pad portrait or less.
        if (windowWidth <= 1024 && !CcmSessionData.Desktop.IsCompactMode) {
            CcmSessionData.Desktop.IsCompactMode = true;
        } else if (windowWidth > 1024 && CcmSessionData.Desktop.IsCompactMode && !this.userSetIsCompactMode) {
            CcmSessionData.Desktop.IsCompactMode = this.userSetIsCompactMode;
        }
        this.emitChanges();
    }

    IsLoginContent(): boolean {
        return !this.userDataService.authorized || this.stateService.current.name.indexOf('Login') === 0;
    }

    isChatShown(): boolean {
        return !this.IsLoginContent() && !this.isMobileAppMode();
    }

    isMobileAppMode() {
        return this.mobileAppHelperService.mobileAppMode;
    }

    private checkLoading(): void {
        document.querySelector('#main_loading').classList.toggle('ng-hide', this.Loaded && !this.globalLoading);
    }

    private ToggleLoading(loading: boolean): void {
        this.globalLoading = isDefined(loading) ? loading : !this.globalLoading;
        this.checkLoading();
    }

    private initStateWatchers(): void {
        this.fiStateChangeService.onSuccess$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.statisticService.trackPageView();
            this.emitChanges();
        });
    }

    private getBodyClass(): string {
        let result = '';
        if (this.IsCompactMode()) result += 'Ccm-Compact ';

        if (this.IsNewsFeedCommentOpened()) result += 'IsNewsFeedCommentOpened ';

        if (this.IsLoginContent() && !this.IsRequestAccessContent() && !this.isFromMobileApp())
            result += 'Ccm-LoginBody ';

        if (this.stateService.current && this.stateService.current.data && this.stateService.current.data.stateClass)
            result += this.stateService.current.data.stateClass + ' ';

        if (this.isMobileAppMode()) result += 'fi-mobile-app ';

        if (isIOS()) result += 'os-ios ';

        return result.trim();
    }

    private IsRequestAccessContent(): boolean {
        return this.stateService.current.name === FiStateNames.Login.RequestAccess;
    }

    private IsCompactMode(): boolean {
        return CcmSessionData.Desktop.IsCompactMode && !this.ShowSearch;
    }

    private IsNewsFeedCommentOpened(): boolean {
        return this.newsFeedService.ShowComment;
    }

    get isAppLanguageLoading$() {
        return this.appTextService.isAppLanguageLoading$;
    }

    SetIsCompactMode(isCompact): void {
        this.isCompactMode = isCompact;
        this.emitChanges();
    }

    @HostListener('window:message', ['$event'])
    onMessage($event: MessageEvent): void {
        if ($event.data && $event.data.OperationEvent === 'ChainformationPlatformAdLogin') {
            this.loadingService.finishLoading();
            this.authModuleService.processChainformationPlatformAdLogin($event.data);
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
